/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { FC, useState, useMemo, useEffect } from "react";
import { Modal } from "components/generals/Modal";
import Button from "components/generals/Button";
import DatePicker from "components/generals/DatePicker";
import { useTranslation } from "react-i18next";
import { 
  Stack, 
  Typography, 
  Divider, 
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField,
  Box
} from "@mui/material";
import { AppointmentInterface, QueryResultInterface, ProviderInterface, PatientInterface, VisitStatusEnum, HeaderEnum } from "@malmur-technologies/medicalsystem-shared";
import AppointmentCard from "components/generals/AppointmentCard";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useMaterialUIController } from "context/ThemeContext";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import { getRangeDateQuery } from "utils/http.utils";
import { 
  getDaysDifference, 
  formatDateKey, 
  formatFullDate, 
  formatDateRange, 
  formatMonthKey, 
  formatWeekKey,
  getWeekStart,
  getWeekEnd,
  getMonthStart,
  getMonthEnd,
  isDateBefore,
  isDateAfter,
  compareDates
} from "utils/date.utils";
import FilterListIcon from "@mui/icons-material/FilterList";
import AppointmentForm from "components/forms/AppointmentForm";
import { FormActionType } from "types";

/**
 * Props for AppointmentDateRangeModal component.
 * @interface AppointmentDateRangeModalProps
 * @property {boolean} open - Whether the modal is open
 * @property {() => void} onClose - Handler to close the modal
 */
interface AppointmentDateRangeModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Interface for grouped appointments by period.
 */
interface GroupedAppointments {
  period: string;
  periodLabel: string;
  appointments: AppointmentInterface[];
}

/**
 * Modal component for searching appointments by date range.
 * Allows users to select a start and end date, then displays appointments
 * grouped by time periods (days, weeks, or months) based on the range.
 * 
 * Features:
 * - Date range selection with validation
 * - Automatic grouping by days, weeks, or months
 * - Display appointments in cards grouped by period
 * - Empty state when no appointments found
 * - Responsive design with dark mode support
 * 
 * @param open - Whether the modal is visible
 * @param onClose - Handler to close the modal
 * @param appointments - List of all appointments to filter and display
 * @returns Date range search modal component
 */
const AppointmentDateRangeModal: FC<AppointmentDateRangeModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [isLoading, _error, sendRequest] = useHttp();
  
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [appointments, setAppointments] = useState<AppointmentInterface[]>([]);
  
  // Filter states
  const [selectedPatient, setSelectedPatient] = useState<PatientInterface | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<ProviderInterface | null>(null);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentInterface | null>(null);
  const [openAppointmentModal, setOpenAppointmentModal] = useState(false);

  const readOnlyStatuses = [
    VisitStatusEnum.CHECK_OUT,
    VisitStatusEnum.CANCEL,
    VisitStatusEnum.NO_SHOW,
  ];
  const [patientList, setPatientList] = useState<PatientInterface[]>([]);
  const [providerList, setProviderList] = useState<ProviderInterface[]>([]);

  // Load patients and providers when modal opens
  useEffect(() => {
    if (open) {
      sendRequest(
        CoreAPI_URLs.PATIENTS_GET_ALL,
        (data: QueryResultInterface<PatientInterface>) => {
          setPatientList(data.results);
        },
        "GET"
      );
      
      sendRequest(
        CoreAPI_URLs.DOCTOR_GET_ALL,
        (data: QueryResultInterface<ProviderInterface>) => {
          setProviderList(data.results);
        },
        "GET"
      );
    }
  }, [open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStartDate(null);
      setEndDate(null);
      setHasSearched(false);
      setErrorMessage("");
      setAppointments([]);
      setSelectedPatient(null);
      setSelectedProvider(null);
    }
  }, [open]);

  // Auto-search when dates or filters change
  useEffect(() => {
    if (startDate && endDate) {
      handleSearch();
    }
  }, [startDate, endDate, selectedPatient, selectedProvider]);

  /**
   * Determines the grouping type based on date range.
   */
  const getGroupingType = (): "day" | "week" | "month" => {
    if (!startDate || !endDate) return "day";
    
    const diffDays = getDaysDifference(endDate, startDate);
    
    if (diffDays <= 7) return "day";
    if (diffDays <= 60) return "week";
    return "month";
  };

  /**
   * Groups appointments by the determined period (day, week, or month).
   */
  const groupedAppointments = useMemo<GroupedAppointments[]>(() => {
    if (!startDate || !endDate || !hasSearched) return [];

    if (appointments.length === 0) return [];

    const groupingType = getGroupingType();
    const grouped = new Map<string, AppointmentInterface[]>();

    appointments.forEach((apt) => {
      let key: string;
      let label: string;

      if (groupingType === "day") {
        key = formatDateKey(apt.arrivalDateTime);
        label = formatFullDate(apt.arrivalDateTime);
      } else if (groupingType === "week") {
        const weekStart = getWeekStart(apt.arrivalDateTime);
        const weekEnd = getWeekEnd(apt.arrivalDateTime);
        key = formatWeekKey(weekStart);
        label = formatDateRange(weekStart, weekEnd);
      } else {
        const monthStart = getMonthStart(apt.arrivalDateTime);
        const monthEnd = getMonthEnd(apt.arrivalDateTime);
        key = formatMonthKey(apt.arrivalDateTime);
        label = formatDateRange(monthStart, monthEnd);
      }

      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(apt);
    });

    // Sort appointments within each group by date
    const result: GroupedAppointments[] = Array.from(grouped.entries())
      .map(([period, appointments]) => {
        const sortedAppointments = appointments.sort((a, b) => 
          compareDates(a.arrivalDateTime, b.arrivalDateTime)
        );
        
        // Get the label from the first and last appointment in the group
        const firstApt = sortedAppointments[0];
        const lastApt = sortedAppointments[sortedAppointments.length - 1];
        let periodLabel: string;

        if (groupingType === "day") {
          periodLabel = formatFullDate(firstApt.arrivalDateTime);
        } else if (groupingType === "week") {
          // Use actual first and last dates from appointments, but constrain to selected range
          const firstMoment = firstApt.arrivalDateTime;
          const lastMoment = lastApt.arrivalDateTime;
          
          const weekStart = isDateBefore(firstMoment, startDate) ? startDate : firstMoment;
          const weekEnd = isDateAfter(lastMoment, endDate) ? endDate : lastMoment;
          
          periodLabel = formatDateRange(weekStart, weekEnd);
        } else {
          // For monthly grouping, use actual first and last dates from appointments, constrain to selected range
          const firstMoment = firstApt.arrivalDateTime;
          const lastMoment = lastApt.arrivalDateTime;
          
          const monthStart = isDateBefore(firstMoment, startDate) ? startDate : firstMoment;
          const monthEnd = isDateAfter(lastMoment, endDate) ? endDate : lastMoment;
          
          periodLabel = formatDateRange(monthStart, monthEnd);
        }

        return {
          period,
          periodLabel,
          appointments: sortedAppointments,
        };
      })
      .sort((a, b) => a.period.localeCompare(b.period));

    return result;
  }, [startDate, endDate, appointments, hasSearched]);

  const handleSearch = () => {
    setErrorMessage("");
    
    if (!startDate || !endDate) {
      setErrorMessage(t("appointmentCalendar.selectDateRange"));
      return;
    }

    if (isDateAfter(startDate, endDate)) {
      setErrorMessage(t("appointmentCalendar.invalidDateRange"));
      return;
    }

    const startDateNative = startDate instanceof Date ? startDate : new Date(startDate);
    const endDateNative = endDate instanceof Date ? endDate : new Date(endDate);
    const query = getRangeDateQuery(startDateNative, endDateNative);
    const fullUrl = CoreAPI_URLs.APPOINTMENTS_GET_ALL + query;
    const headers: Record<string, string> = {};
    
    if (selectedPatient) {
      headers[HeaderEnum.PATIENT_ID] = selectedPatient.id;
    }
    
    if (selectedProvider) {
      headers[HeaderEnum.PROVIDER_ID] = selectedProvider.id;
    }
    
    sendRequest(
      fullUrl,
      (data: QueryResultInterface<AppointmentInterface>) => {
        setAppointments(data?.results || []);
        setHasSearched(true);
      },
      "GET",
      undefined,
      headers,
      {
        applyErrorFn: (errorMessage: string) => {
          setErrorMessage(t("appointmentCalendar.errorFetchingAppointments"));
          setHasSearched(true);
        }
      }
    );
  };
  const handleAppointmentClick = (appointment: AppointmentInterface) => {
    setSelectedAppointment(appointment);
    setOpenAppointmentModal(true);
  };
  const handleCloseAppointmentModal = () => {
    setOpenAppointmentModal(false);
    setSelectedAppointment(null);
  };
  const handleSaveAppointment = (updatedAppointment: AppointmentInterface) => {
    setAppointments(prevAppointments => 
      prevAppointments.map(apt => 
        apt.id === updatedAppointment.id ? updatedAppointment : apt
      )
    );
    handleCloseAppointmentModal();
  };

  return (
    <>
      <Modal open={open} onClose={onClose} maxWidth="900px" minWidth="300px">
        <div className={`appointment-modal__wrapper ${darkMode ? 'dark-mode' : ''}`}>
        <Stack spacing={2} sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="bold" color={darkMode ? "white" : "textPrimary"}>
              {t("appointmentCalendar.dateRangeSearch")}
            </Typography>
          </Stack>
          {/* Date Range Selection */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                id="cy-startDate-picker"
                label={t("appointmentCalendar.startDate")}
                value={startDate}
                onChange={(newValue) => {
                  setStartDate(newValue);
                  setHasSearched(false);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                id="cy-endDate-picker"
                label={t("appointmentCalendar.endDate")}
                value={endDate}
                options={{ minDate: startDate instanceof Date ? startDate : (startDate ? new Date(startDate) : undefined) }}
                onChange={(newValue) => {
                  setEndDate(newValue);
                  setHasSearched(false);
                }}
              />
            </Grid>
          </Grid>
          {hasSearched && (
            <div>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                <FilterListIcon color="primary" fontSize="small" />
                <Typography variant="body2" fontWeight="bold" color={darkMode ? "white" : "textPrimary"}>
                  {t("appointmentCalendar.moreFilterOptions")}
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={patientList}
                    value={selectedPatient}
                    onChange={(_, newValue) => {
                      setSelectedPatient(newValue);
                    }}
                    getOptionLabel={(option) => 
                      `${option.person?.name || ""} ${option.person?.lastname || ""}`
                    }
                    size="small"
                    disabled={isLoading}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label={t("appointmentCalendar.filterByPatient")}
                        variant="standard"
                        fullWidth
                        disabled={isLoading}
                        className={`autocomplete-textfield ${darkMode ? 'dark-mode' : ''}`}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={providerList}
                    value={selectedProvider}
                    onChange={(_, newValue) => {
                      setSelectedProvider(newValue);
                    }}
                    getOptionLabel={(option) => 
                      `Dr. ${option.person?.name || ""} ${option.person?.lastname || ""}`
                    }
                    size="small"
                    disabled={isLoading}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label={t("appointmentCalendar.filterByDoctor")}
                        variant="standard"
                        fullWidth
                        disabled={isLoading}
                        className={`autocomplete-textfield ${darkMode ? 'dark-mode' : ''}`}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </div>
          )}
          {errorMessage && (
            <Alert severity="error">{errorMessage}</Alert>
          )}
          {isLoading ? (
            <div className="daterange-modal__loading">
              <CircularProgress size={50} />
              <Typography variant="body1" color="text.secondary">
                {t("common.loading")}...
              </Typography>
            </div>
          ) : hasSearched && (
            <div className="daterange-modal__results">
              {groupedAppointments.length === 0 ? (
                <Alert severity="info">
                  {t("appointmentCalendar.noAppointmentsFound")}
                </Alert>
              ) : (
                <Stack spacing={1.5} className="daterange-modal__results-inner">
                  <Typography variant="h6" fontWeight="bold" color={darkMode ? "white" : "textPrimary"}>
                    {t("appointmentCalendar.appointmentsInRange")} ({groupedAppointments.reduce((acc, g) => acc + g.appointments.length, 0)})
                  </Typography>
                  
                  <div className="daterange-modal__scroll-area">
                    <Stack spacing={2}>
                      {groupedAppointments.map((group) => (
                        <Card
                          key={group.period}
                          sx={{
                            backgroundColor: darkMode ? "#2a2a2a" : "#f5f5f5",
                          }}
                        >
                          <CardContent className="daterange-modal__card-content-appointment-item">
                            <Stack spacing={2}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="h6" fontWeight="bold" color={darkMode ? "white" : "textPrimary"}>
                                  📅 {group.periodLabel}
                                </Typography>
                                <Chip 
                                  label={group.appointments.length} 
                                  size="small" 
                                  color="primary" 
                                />
                              </Stack>
                              
                              <Divider />
                              
                              <Stack spacing={0.5}>
                                {group.appointments.map((appointment) => (
                                  <div key={appointment.id} className="daterange-modal__appointment-item">
                                    <AppointmentCard
                                      appointment={appointment}
                                      onClick={() => handleAppointmentClick(appointment)}
                                      showDate={true}
                                    />
                                  </div>
                                ))}
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </div>
                </Stack>
              )}
            </div>
          )}
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            gap={2}
            pt={2}
          >
            <Button 
              color="secondary" 
              onClick={onClose}
            >
              {t("appointmentCalendar.close")}
            </Button>
          </Box>
        </Stack>
      </div>
    </Modal>
    {selectedAppointment && (
      <Modal
        open={openAppointmentModal}
        onClose={handleCloseAppointmentModal}
        maxWidth="800px"
      >
        <div>
          <AppointmentForm
            model={selectedAppointment}
            updateListHandler={handleSaveAppointment}
            onCloseAction={handleCloseAppointmentModal}
            action={readOnlyStatuses.includes(selectedAppointment.visitStatus) ? FormActionType.READ_ONLY : FormActionType.MODIFY}
            isModal={true}
          />
        </div>
      </Modal>
    )}
    </>
  );
};

export default AppointmentDateRangeModal;
